import { memo, useCallback, useState } from 'react';

import { FileUrl, GADataType } from '../../../../../types';
import { ModelViewerEditorRefType } from '../../content/ModelViewerEditor/ModelViewerEditor.types';

import { useModal } from '../../../../../helpers/modals/hooks/useModal';

import { ModelViewerProps } from '../../../../../helpers/ModelViewer';
import { SimpleModalButtonDefaultProps } from '../../../../../helpers/buttons/SimpleModalButton';
import { PureTooltipIconButtonHelper } from '../../../../../helpers/buttons/PureTooltipIconButtonHelper';
import { SimpleModal } from '../../../../../helpers/modals/SimpleModal';
import { ModelViewerEditor } from '../../content/ModelViewerEditor';
import { ModelViewerCloseConfirmationModal } from './components/ModelViewerCloseConfirmationModal';

type ModelViewerModalButtonProps = Pick<
  SimpleModalButtonDefaultProps,
  | 'className'
  | 'i18nText'
  | 'i18nTitle'
  | 'title'
  | 'icon'
  | 'iconClassName'
  | 'tooltipPlacement'
  | 'tooltipI18nText'
  | 'modalSize'
> &
  Pick<ModelViewerProps, 'cameraControls'> & {
    withDownloadButton?: boolean;
    productNanoId?: string | null;
    model?: {
      file: FileUrl;
      name: string;
    };
    initialModelIndex?: number;
    models?: {
      file: FileUrl;
      name: string;
    }[];
  };

function ModelViewerModalButton({
  className,
  dataGa,
  title,
  i18nTitle,
  i18nText,
  icon,
  iconClassName,
  tooltipPlacement,
  tooltipI18nText,
  modalSize,
  model,
  initialModelIndex,
  models,
  cameraControls,
  withDownloadButton,
  productNanoId
}: ModelViewerModalButtonProps & GADataType) {
  const [modelViewerEditorRef, setModelViewerEditorRef] =
    useState<ModelViewerEditorRefType>(null);
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] =
    useState<boolean>(false);

  const { isOpen, showModal, hideModal } = useModal();

  // Every exit path (header X, overlay click, Esc) funnels through here: when
  // the session has at least one generation, ask for confirmation first.
  const handleCloseRequest = useCallback<() => void>(() => {
    if (modelViewerEditorRef?.generationsCount > 0) {
      setIsCloseConfirmationOpen(true);
      return;
    }

    hideModal();
  }, [modelViewerEditorRef, hideModal]);

  const handleConfirmationGoBack = useCallback<() => void>(
    () => setIsCloseConfirmationOpen(false),
    []
  );

  const handleConfirmationCloseAnyway = useCallback<() => void>(() => {
    setIsCloseConfirmationOpen(false);
    hideModal();
  }, [hideModal]);

  return (
    <>
      <PureTooltipIconButtonHelper
        className={className}
        dataGa={dataGa}
        icon={icon}
        iconClassName={iconClassName}
        i18nText={i18nText}
        tooltipI18nText={tooltipI18nText}
        tooltipPlacement={tooltipPlacement}
        onClick={showModal}
      />

      <SimpleModal
        addModalClassName="h-full overflow-hidden"
        buttonsContainerClassName="p-0"
        childrenClassName="w-full flex-1 min-h-0 relative overflow-hidden"
        dataGa={dataGa}
        hideModal={handleCloseRequest}
        i18nTitle={i18nTitle}
        isOpen={isOpen}
        modalSize={modalSize || 'full'}
        title={title ?? modelViewerEditorRef?.activeItem?.name}
        withoutCancelButton
        withoutSubmitButton
      >
        <ModelViewerEditor
          ref={setModelViewerEditorRef}
          model={model}
          initialModelIndex={initialModelIndex}
          models={models}
          cameraControls={cameraControls}
          title={title}
          withDownloadButton={withDownloadButton}
          productNanoId={productNanoId}
        />
        <ModelViewerCloseConfirmationModal
          isOpen={isCloseConfirmationOpen}
          onGoBack={handleConfirmationGoBack}
          onCloseAnyway={handleConfirmationCloseAnyway}
        />
      </SimpleModal>
    </>
  );
}

export default memo(ModelViewerModalButton);
